import moment from 'moment-timezone';
import { ianaTimezones } from '../../../constants';

/**
 * IST conversion for the Events tab. The zone is a CONSTANT, never the header
 * timezone dropdown — that defaults to US Pacific and resets on reload, which would
 * silently store a slot 12.5 hours off with the field still reading "3:00 PM".
 */

export const IST_ZONE = 'Asia/Kolkata';

export const ist_wall_clock_to_epoch = (run_date, time_hhmm) =>
  moment.tz(`${run_date} ${time_hhmm}`, 'YYYY-MM-DD HH:mm', IST_ZONE).valueOf();

export const epoch_to_ist_wall_clock = (epoch_ms) => moment(epoch_ms).tz(IST_ZONE).format('HH:mm');

export const utc_label = (epoch_ms) => `${moment(epoch_ms).utc().format('HH:mm')}Z`;

/**
 * The region for a column header. The dropdown's own labels carry a hardcoded
 * abbreviation such as 'Pacific (PST)', which is wrong for half the year, and a
 * header has no instant to derive the right one from. The abbreviation goes on each
 * cell instead.
 */
export const zone_label = (zone) => {
  const group = ianaTimezones.find((entry) =>
    entry.options.some((option) => option.value === zone)
  );
  if (!group) return zone;

  const { label } = group.options.find((option) => option.value === zone);
  // Stripping to the end rather than to a matching bracket, because one label in the
  // list ('Brisbane (AEST') never closes its parenthesis.
  const region = label
    .replace(/\s*\(.*$/, '')
    .replace(/_/g, ' ')
    .trim();

  // 'IST' and 'GMT' are abbreviations rather than places, so fall back to the group.
  return !region || /^[A-Z+\-0-9:]{1,5}$/.test(region) ? group.label : region;
};

/**
 * The abbreviation for a zone at a given instant, so it stays correct on both sides
 * of a daylight-saving transition. Zones with no common abbreviation return a bare
 * offset such as '+04', which is normalised to 'GMT+4'.
 */
export const zone_abbrev = (instant, zone) => {
  const raw = moment(instant).tz(zone).format('z');
  const bare = raw.match(/^([+-])(\d{2}):?(\d{2})?$/);
  if (!bare) return raw;

  const [, sign, hours, minutes] = bare;
  const hour = String(Number(hours));
  return `GMT${sign}${hour}${minutes && minutes !== '00' ? `:${minutes}` : ''}`;
};

/**
 * Renders a slot in the given zone, with a day marker when it falls on a different
 * calendar date than the run's IST date. The same instant really is tomorrow in
 * Sydney, and a bare wall clock would hide that.
 */
export const in_zone = (instant, zone, run_date) => {
  const local = moment(instant).tz(zone);
  const time = `${local.format('h:mm A')} ${zone_abbrev(instant, zone)}`;
  if (!run_date) return time;

  // Compared as date strings, because diffing a zone-aware moment against a locally
  // parsed one mixes frames and silently returns the wrong day count.
  const local_date = local.format('YYYY-MM-DD');
  if (local_date === run_date) return time;

  const days = moment(local_date, 'YYYY-MM-DD').diff(moment(run_date, 'YYYY-MM-DD'), 'days');
  return `${time} (${days > 0 ? '+' : ''}${days}d)`;
};
